import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DEMO_COMMUNITY, 
  DEMO_BADGES, 
  getDemoHolderCount,
  DEMO_PUBKEYS,
  getDemoProfile 
} from '@/lib/demoData';

const Index = () => {
  useSeoMeta({
    title: 'Hierarchical Badges - NIP-XX Demo',
    description: 'A decentralized authority system for Nostr communities with delegated badge granting',
  });

  const holders = getDemoHolderCount();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/50 rounded-full blur-[128px] animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-indigo-500/50 rounded-full blur-[128px] animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-violet-500/30 rounded-full blur-[128px] animate-pulse delay-500" />
        </div>
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Navigation */}
        <nav className="border-b border-white/10 backdrop-blur-sm bg-black/20">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <ShieldIcon className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-lg text-white">Hierarchical Badges</span>
            </div>
            <Badge variant="outline" className="bg-white/10 text-white border-white/20 backdrop-blur">
              NIP-XX Draft
            </Badge>
          </div>
        </nav>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70 mb-8 backdrop-blur">
            <SparklesIcon className="w-4 h-4 text-amber-400" />
            Built on Nostr Protocol
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-purple-400 via-indigo-400 to-violet-400 bg-clip-text text-transparent">
              Hierarchical Badges
            </span>
          </h1>
          
          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-12 leading-relaxed">
            A decentralized authority system for Nostr communities. 
            Establish ranked tiers of trust with delegated badge granting, 
            chain validation, and automatic revocation propagation.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white border-0 shadow-xl shadow-purple-500/20">
              <Link to="/badges">
                <ExploreIcon className="w-5 h-5 mr-2" />
                Explore Demo
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 backdrop-blur">
              <Link to={`/badges/${DEMO_COMMUNITY.id}`}>
                <CommunityIcon className="w-5 h-5 mr-2" />
                View Community
              </Link>
            </Button>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '1', label: 'Community', icon: <CommunityIcon className="w-5 h-5" /> },
              { value: '5', label: 'Badge Tiers', icon: <LayersIcon className="w-5 h-5" /> },
              { value: String(holders), label: 'Members', icon: <UsersIcon className="w-5 h-5" /> },
              { value: '8', label: 'Authority Chains', icon: <ChainIcon className="w-5 h-5" /> },
            ].map((stat, i) => (
              <div 
                key={i} 
                className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-3 text-white/60 mb-2">
                  {stat.icon}
                  <span className="text-sm font-medium">{stat.label}</span>
                </div>
                <p className="text-4xl font-bold text-white">{stat.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Authority flows from founders through a hierarchy of ranks, 
              with each level able to grant badges to lower ranks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              {
                rank: 0,
                title: 'Founders',
                description: 'Origin of authority, can award any badge',
                color: 'from-amber-500/20 to-orange-500/20',
                borderColor: 'border-amber-500/30',
                badgeBg: 'bg-amber-500',
              },
              {
                rank: 1,
                title: 'Maintainers',
                description: 'Award Contributor and Member badges',
                color: 'from-purple-500/20 to-violet-500/20',
                borderColor: 'border-purple-500/30',
                badgeBg: 'bg-purple-500',
              },
              {
                rank: 2,
                title: 'Contributors',
                description: 'Award Member badges',
                color: 'from-emerald-500/20 to-teal-500/20',
                borderColor: 'border-emerald-500/30',
                badgeBg: 'bg-emerald-500',
              },
              {
                rank: 3,
                title: 'Members',
                description: 'Base level, no awarding authority',
                color: 'from-blue-500/20 to-cyan-500/20',
                borderColor: 'border-blue-500/30',
                badgeBg: 'bg-blue-500',
              },
            ].map((tier, i) => (
              <div 
                key={i}
                className={`relative p-6 rounded-2xl bg-gradient-to-br ${tier.color} border ${tier.borderColor} backdrop-blur-sm`}
              >
                <div className={`absolute -top-3 left-6 ${tier.badgeBg} w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white`}>
                  {tier.rank}
                </div>
                <h3 className="font-semibold text-white mt-2 mb-2">{tier.title}</h3>
                <p className="text-sm text-white/60">{tier.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Demo Community Preview */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="rounded-3xl bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <img
                src={DEMO_COMMUNITY.image}
                alt={DEMO_COMMUNITY.name}
                className="w-32 h-32 rounded-2xl border-4 border-white/20 shadow-2xl object-cover"
              />
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium mb-3">
                  <StarIcon className="w-4 h-4" />
                  Demo Community
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{DEMO_COMMUNITY.name}</h3>
                <p className="text-white/70 mb-4">{DEMO_COMMUNITY.description}</p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {DEMO_BADGES.slice(0, 4).map(badge => (
                    <span 
                      key={badge.identifier}
                      className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-sm backdrop-blur"
                    >
                      {badge.name}
                    </span>
                  ))}
                </div>
              </div>
              <Button asChild size="lg" className="shrink-0">
                <Link to={`/badges/${DEMO_COMMUNITY.id}`}>
                  Explore
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Featured Members */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Featured Members</h2>
            <p className="text-white/60">Some of the badge holders in our demo community</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[DEMO_PUBKEYS.alice, DEMO_PUBKEYS.carol, DEMO_PUBKEYS.eve, DEMO_PUBKEYS.iris].map(pubkey => {
              const profile = getDemoProfile(pubkey);
              return (
                <Link 
                  key={pubkey}
                  to={`/badges/user/${pubkey}`}
                  className="group p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all hover:scale-105"
                >
                  <img
                    src={profile.picture}
                    alt={profile.name}
                    className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-transparent group-hover:border-purple-400 transition-colors"
                  />
                  <p className="text-white font-medium text-center text-sm truncate">
                    {profile.name}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <p className="text-white/40 text-sm">
              Vibed with{" "}
              <a 
                href="https://shakespeare.diy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                Shakespeare
              </a>
              {" "}• Hierarchical Badges Demo (NIP-XX Draft)
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;

// Icons
function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 13l.75 2.25L22 16l-2.25.75L19 19l-.75-2.25L16 16l2.25-.75L19 13z" />
      <path d="M5 17l.5 1.5L7 19l-1.5.5L5 21l-.5-1.5L3 19l1.5-.5L5 17z" />
    </svg>
  );
}

function ExploreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

function CommunityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function ChainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
